import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { storage } from '../../../config/firebase';

export interface MediaPreview {
  url: string;
  type: 'image' | 'video' | 'gif' | 'audio' | 'file';
  progress?: number;
  error?: string;
  message?: string;
}

/**
 * Hook to handle media uploads for support chat
 */
export const useSupportMedia = () => {
  const [mediaPreview, setMediaPreview] = useState<MediaPreview | null>(null);

  /**
   * Upload file to Firebase Storage and generate preview
   */
  const handleFileSelect = useCallback(async (file: File) => {
    const acceptedImageTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    const acceptedVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    const acceptedAudioTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg'];

    let mediaType: MediaPreview['type'] = 'file';

    // Determine the media type from the file MIME type
    if (acceptedImageTypes.includes(file.type)) {
      mediaType = 'image';
    } else if (acceptedVideoTypes.includes(file.type)) {
      mediaType = 'video';
    } else if (acceptedAudioTypes.includes(file.type)) {
      mediaType = 'audio';
    }

    // Set temporary preview
    const tempUrl = URL.createObjectURL(file);
    setMediaPreview({
      url: tempUrl,
      type: mediaType,
      progress: 0,
    });

    // Create a file reference with a unique name
    const fileId = uuidv4();
    const fileExt = file.name.split('.').pop() || '';
    const storageRef = ref(storage, `support/${fileId}.${fileExt}`);

    try {
      // Start upload with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Track upload progress
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setMediaPreview((prev) => (prev ? { ...prev, progress } : null));
        },
        (error) => {
          // Handle upload error
          console.error('Upload error:', error);
          setMediaPreview((prev) =>
            prev ? { ...prev, error: 'Failed to upload file' } : null
          );
        },
        async () => {
          // Upload completed successfully, get download URL
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          setMediaPreview((prev) =>
            prev ? { ...prev, url: downloadUrl, progress: 100 } : null
          );
        }
      );
    } catch (error) {
      console.error('Error uploading file:', error);
      setMediaPreview((prev) =>
        prev ? { ...prev, error: 'Failed to upload file' } : null
      );
    }
  }, []);

  /**
   * Handle GIF selection
   */
  const handleGifSelect = useCallback((gifUrl: string, message?: string) => {
    setMediaPreview({
      url: gifUrl,
      type: 'gif',
      progress: 100,
      message,
    });
    return true;
  }, []);

  /**
   * Clear media preview
   */
  const removeMedia = useCallback(() => {
    setMediaPreview(null);
  }, []);

  return {
    mediaPreview,
    setMediaPreview,
    handleFileSelect,
    handleGifSelect,
    removeMedia,
  };
};
